import axiosInstance from './axiosInstance';
import { tokenService } from './tokenService';

const API_URL = '/api/v1/profile';
const PROFILE_URL = `${API_URL}/`;

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ProfileImageResponse {
  profile_image_url: string;
}

function appendCropData(formData: FormData, cropData: CropData): void {
  formData.append('crop_x', cropData.x.toString());

  formData.append('crop_y', cropData.y.toString());

  formData.append('crop_width', cropData.width.toString());

  formData.append('crop_height', cropData.height.toString());
}

function createFormDataWithCrop(file: File, cropData: CropData): FormData {
  const formData = new FormData();

  formData.append('file', file);

  appendCropData(formData, cropData);

  return formData;
}

// Transform relative profile image URL to absolute URL
const transformProfileImageUrl = (url: string | undefined | null): string | undefined => {
  if (!url) return undefined;

  const apiBaseUrl = process.env.REACT_APP_API_URL || '';
  const accessToken = tokenService.getAccessToken();
  const tokenParam = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';

  return `${apiBaseUrl}${url}${tokenParam}`;
};

export const profileService = {
  getProfile: async () => {
    const response = await axiosInstance.get(PROFILE_URL);

    const data = response.data;

    // Transform profile_image_url to absolute URL
    if (data.profile_image_url) {
      data.profile_image_url = transformProfileImageUrl(data.profile_image_url);
    }

    return data;
  },

  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    display_name?: string;
    country?: string;
    state_code?: string | null;
  }) => {
    const response = await axiosInstance.put(PROFILE_URL, data);

    return response.data;
  },

  changePassword: async (data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) => {
    const response = await axiosInstance.post(`${API_URL}/change-password`, data);

    return response.data;
  },

  changeEmail: async (data: { new_email: string; password: string }) => {
    const response = await axiosInstance.post(`${API_URL}/change-email`, data);

    return response.data;
  },

  uploadProfileImage: async (file: File, cropData: CropData): Promise<ProfileImageResponse> => {
    const formData = createFormDataWithCrop(file, cropData);

    const response = await axiosInstance.post(`${API_URL}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return {
      profile_image_url: transformProfileImageUrl(response.data.profile_image_url) || '',
    };
  },

  deleteProfileImage: async (): Promise<void> => {
    await axiosInstance.delete(`${API_URL}/image`);
  },

  // BYOK Key Management
  getBYOKKeys: async () => {
    const response = await axiosInstance.get(`${API_URL}/byok-keys`);

    return response.data;
  },

  addBYOKKey: async (label: string, token: string) => {
    const response = await axiosInstance.post(`${API_URL}/byok-keys`, { label, token });

    return response.data;
  },

  deleteBYOKKey: async (keyIndex: number) => {
    const response = await axiosInstance.delete(`${API_URL}/byok-keys/${keyIndex}`);

    return response.data;
  },

  // OpenClaw API Key Management
  generateOpenClawKey: async (expiresIn: string, name?: string) => {
    const response = await axiosInstance.post(`${API_URL}/openclaw-key`, {
      name: name || '',
      expires_in: expiresIn,
    });

    return response.data;
  },

  getOpenClawKeyStatus: async () => {
    const response = await axiosInstance.get(`${API_URL}/openclaw-key`);

    return response.data;
  },

  deleteOpenClawKey: async () => {
    const response = await axiosInstance.delete(`${API_URL}/openclaw-key`);

    return response.data;
  },
};
